/**
 * Create-attempt recovery for the Product Create wizard (B-008).
 *
 * A product create is a sequence. Once `create_product` has confirmed a product
 * the operator must never be able to create it a second time, even if the
 * post-create steps (media transfer, supplier link, channel drafts) did not
 * finish and the browser is reloaded. The submission lock that enforces that in
 * memory is a React ref, so it does not survive a reload.
 *
 * This module owns the small, durable record that does survive: which product
 * belongs to which draft, and what still needs doing. It holds no secrets and
 * no `form` state — the draft itself is the recovery payload.
 *
 * Rules encoded here:
 *   - a record only applies to the exact organisation + draft it was written for;
 *   - a record is written BEFORE `create_product` runs (`pending_create`), so a
 *     reload between the server commit and the response handler reuses the same
 *     idempotency key instead of minting a new one;
 *   - a missing product id is never evidence that creation failed — it is an
 *     unresolved outcome that must be reconciled or retried with that same key;
 *   - a record with a product id always blocks a new create for that draft;
 *   - the draft is kept while any post-create obligation is unresolved, because
 *     it is the only copy of the media and channel selections a retry needs;
 *   - draft cleanup failure is never reported as a product failure.
 */

import { isLocalValidationError } from "@/lib/errors";

export type CreateRecoveryStage = "pending_create" | "canonical" | "media" | "post_create";

export interface CreateRecoveryRecord {
  version: 1;
  orgId: string;
  draftId: string;
  /** Empty ONLY for a `pending_create` record, where the outcome is not yet known. */
  productId: string;
  productName: string;
  idemKey: string;
  stage: CreateRecoveryStage;
  createdAt: string;
  /**
   * Advisory marker for the page instance that last wrote this record.
   *
   * It is NOT an atomic lock and must never be treated as one. `localStorage`
   * has no compare-and-swap, no transaction and no ordering guarantee between
   * tabs. It only records who wrote last so a peer can refuse destructive
   * operations against an existing record.
   */
  attemptId?: string;
  /** Last write time, for diagnostics and conservative staleness handling. */
  updatedAt?: string;
}

/** Minimal storage surface so this can be tested without a browser. */
export interface RecoveryStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const KEY_PREFIX = "tradivo:wizard-create-recovery";

export function createRecoveryStorageKey(orgId: string, draftId: string): string {
  return `${KEY_PREFIX}:${orgId}:${draftId}`;
}

/** Local storage when available; null in tests, SSR, or a locked-down browser. */
export function browserRecoveryStorage(): RecoveryStore | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    // Storage can throw on access in private mode. Recovery then depends on the
    // live session only; the operator is told rather than silently misled.
    return null;
  }
}

const STAGES: CreateRecoveryStage[] = ["pending_create", "canonical", "media", "post_create"];

/**
 * Reject anything that is not a complete record for this exact org + draft.
 * A record for another draft, another organisation, or a corrupted payload must
 * never be applied — that would bind the wrong product to this wizard.
 *
 * A `pending_create` record legitimately has no product id: it was written
 * before the create RPC, and its whole purpose is to keep the key. Any other
 * stage without a product id is malformed.
 */
export function parseCreateRecovery(
  raw: string | null | undefined,
  expected: { orgId: string; draftId: string },
): CreateRecoveryRecord | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const row = parsed as Partial<CreateRecoveryRecord>;
  if (row.version !== 1) return null;
  if (typeof row.orgId !== "string" || row.orgId !== expected.orgId) return null;
  if (typeof row.draftId !== "string" || row.draftId !== expected.draftId) return null;
  if (typeof row.stage !== "string" || !STAGES.includes(row.stage as CreateRecoveryStage)) return null;
  const stage = row.stage as CreateRecoveryStage;
  const productId = typeof row.productId === "string" ? row.productId : "";
  const idemKey = typeof row.idemKey === "string" ? row.idemKey : "";
  if (stage === "pending_create") {
    // Without a key the record cannot do its only job.
    if (!idemKey) return null;
  } else if (productId.length === 0) {
    return null;
  }
  return {
    version: 1,
    orgId: row.orgId,
    draftId: row.draftId,
    productId,
    productName: typeof row.productName === "string" ? row.productName : "",
    idemKey,
    stage,
    createdAt: typeof row.createdAt === "string" ? row.createdAt : "",
    ...(typeof row.attemptId === "string" && row.attemptId ? { attemptId: row.attemptId } : {}),
    ...(typeof row.updatedAt === "string" && row.updatedAt ? { updatedAt: row.updatedAt } : {}),
  };
}

/**
 * A store that can also be enumerated. `localStorage` satisfies this; a test
 * double only needs to opt in.
 */
export interface EnumerableRecoveryStore extends RecoveryStore {
  readonly length?: number;
  key?(index: number): string | null;
}

/**
 * Outstanding create recoveries for one exact organisation + product.
 *
 * A product created by the wizard can be left with unfinished post-create work
 * (images still on the draft, a channel draft that failed, an unlinked
 * supplier). That record lives in this browser, keyed by draft, so opening the
 * product used to show nothing at all and the outstanding work was only visible
 * by returning to the draft manually.
 *
 * Read-only and fail-closed: a store that cannot be enumerated, or bytes that
 * cannot be parsed, produce no claim. Nothing is written, cleared or repaired.
 */
export function findCreateRecoveriesForProduct(
  store: EnumerableRecoveryStore | null,
  orgId: string | null | undefined,
  productId: string | null | undefined,
): CreateRecoveryRecord[] {
  if (!store || !orgId || !productId) return [];
  if (typeof store.key !== "function") return [];
  const total = typeof store.length === "number" ? store.length : 0;
  if (total <= 0) return [];

  const prefix = `${KEY_PREFIX}:${orgId}:`;
  const found: CreateRecoveryRecord[] = [];
  for (let index = 0; index < total; index += 1) {
    let key: string | null = null;
    try {
      key = store.key(index);
    } catch {
      continue;
    }
    if (!key || !key.startsWith(prefix)) continue;
    const draftId = key.slice(prefix.length);
    if (!draftId) continue;
    let raw: string | null = null;
    try {
      raw = store.getItem(key);
    } catch {
      continue;
    }
    const record = parseCreateRecovery(raw, { orgId, draftId })
      ?? (raw ? adoptHistoricalCreateRecovery(raw, { orgId, draftId }) : null);
    // Only a resolved record can describe a product. An unresolved create has
    // no product to attribute, so it is never shown against one.
    if (!record || record.productId !== productId) continue;
    found.push(record);
  }
  return found.sort((a, b) => (b.updatedAt ?? b.createdAt).localeCompare(a.updatedAt ?? a.createdAt));
}

/**
 * The three states of a recovery slot. Absence and unreadability are different
 * facts and must never collapse into one another:
 *
 *   - `absent`     — nothing was written for this draft. Creation is unrestricted.
 *   - `valid`      — a record exists and can be interpreted.
 *   - `unreadable` — bytes exist but cannot be interpreted. The earlier creation
 *                    outcome cannot be established, so the slot is evidence, not
 *                    an empty slot. It must never be overwritten or cleared.
 */
export type RecoverySlotState =
  | { state: "absent" }
  | { state: "valid"; record: CreateRecoveryRecord }
  | { state: "unreadable"; reason: string };

/**
 * Adopt a parseable historical record whose identity is still intact.
 *
 * An incompatible *shape* is not the same as lost identity. Where the record
 * still names this exact org + draft and still carries its creation key, the
 * key is preserved and the stage is inferred conservatively:
 *   - a product id present  → `canonical` (the safest resumable stage: a retry
 *     also replays the media transfer and the channel steps);
 *   - no product id         → `pending_create` (reuse the key, never re-mint).
 *
 * Anything that lost its identity fails closed at the caller.
 */
function adoptHistoricalCreateRecovery(
  raw: string,
  expected: { orgId: string; draftId: string },
): CreateRecoveryRecord | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const row = parsed as Record<string, unknown>;
  if (row.orgId !== expected.orgId || row.draftId !== expected.draftId) return null;
  const idemKey = typeof row.idemKey === "string" ? row.idemKey.trim() : "";
  if (!idemKey) return null;
  const productId = typeof row.productId === "string" ? row.productId : "";
  const declared = typeof row.stage === "string" && STAGES.includes(row.stage as CreateRecoveryStage)
    ? (row.stage as CreateRecoveryStage)
    : null;
  const stage: CreateRecoveryStage = declared ?? (productId ? "canonical" : "pending_create");
  if (stage !== "pending_create" && !productId) return null;
  return {
    version: 1,
    orgId: expected.orgId,
    draftId: expected.draftId,
    productId,
    productName: typeof row.productName === "string" ? row.productName : "",
    idemKey,
    stage,
    createdAt: typeof row.createdAt === "string" ? row.createdAt : "",
    ...(typeof row.attemptId === "string" && row.attemptId ? { attemptId: row.attemptId } : {}),
    ...(typeof row.updatedAt === "string" && row.updatedAt ? { updatedAt: row.updatedAt } : {}),
  };
}

/**
 * Read the recovery slot for one exact org + draft.
 *
 * A non-empty value that cannot be read or validated is reported as
 * `unreadable` — never as `absent`. Interpreting corruption as absence is what
 * let an unknown creation outcome be treated as "nothing exists", which derives
 * a fresh identity and is the duplicate window this module exists to close.
 */
export function readCreateRecoveryState(
  store: RecoveryStore | null,
  orgId: string | null | undefined,
  draftId: string | null | undefined,
): RecoverySlotState {
  // No scope, or no store at all: nothing can be attributed to this draft, so
  // nothing is claimed. (A missing store is blocked separately by the
  // write-ahead confirmation, not by invented absence here.)
  if (!store || !orgId || !draftId) return { state: "absent" };
  let raw: string | null;
  try {
    raw = store.getItem(createRecoveryStorageKey(orgId, draftId));
  } catch {
    // The slot exists but cannot be read. That is not proven absence.
    return {
      state: "unreadable",
      reason: "This browser refused to read the create recovery record for this draft.",
    };
  }
  if (raw === null || raw.trim() === "") return { state: "absent" };
  const record = parseCreateRecovery(raw, { orgId, draftId })
    ?? adoptHistoricalCreateRecovery(raw, { orgId, draftId });
  if (!record) {
    return {
      state: "unreadable",
      reason: "The stored create recovery record for this draft could not be read.",
    };
  }
  return { state: "valid", record };
}

/**
 * Record-only view of the slot, kept for callers that do not need to tell
 * absence from unreadability. Anything that decides whether creation may run
 * must use `readCreateRecoveryState` (or `evaluateCreateEligibility`) instead.
 */
export function readCreateRecovery(
  store: RecoveryStore | null,
  orgId: string | null | undefined,
  draftId: string | null | undefined,
): CreateRecoveryRecord | null {
  const slot = readCreateRecoveryState(store, orgId, draftId);
  return slot.state === "valid" ? slot.record : null;
}

/**
 * Low-level unconditional write.
 *
 * This has NO conflict detection and no atomicity: it is kept for tests and for
 * building fixtures, so the "read then write is not a lock" limitation stays
 * demonstrable. Production writes go through `writePendingCreateRecovery`
 * (pre-submit) or `writeResolvedCreateRecovery` (after a product is known).
 */
export function writeCreateRecovery(
  store: RecoveryStore | null,
  record: Omit<CreateRecoveryRecord, "version"> & { version?: 1 },
): boolean {
  if (!store) return false;
  try {
    store.setItem(
      createRecoveryStorageKey(record.orgId, record.draftId),
      JSON.stringify({ ...record, version: 1 }),
    );
    return true;
  } catch {
    return false;
  }
}

export type ResolvedRecoveryWrite = "written" | "conflict" | "unreadable" | "unavailable";

/**
 * Write a RESOLVED record (a confirmed product id plus the stage it reached)
 * without destroying another creation identity.
 *
 * Every resolved write in the wizard goes through here. A late response from one
 * tab must never erase another tab's unresolved creation identity, so the rules
 * are conservative and refusal always beats an optimistic merge:
 *
 *   - an unreadable slot is refused outright (bytes are evidence, not garbage);
 *   - a product-less record is refused (a resolved write must carry a product);
 *   - another unresolved creation identity with a DIFFERENT key is refused —
 *     that attempt may still be waiting on a create that could commit;
 *   - a different product already recorded for the same draft is refused;
 *   - the same key upgrading pending → resolved is permitted (that is the
 *     write-ahead upgrade path and the legacy-key adoption path).
 *
 * On conflict the existing record is left byte-for-byte unchanged.
 */
export function writeResolvedCreateRecovery(
  store: RecoveryStore | null,
  record: Omit<CreateRecoveryRecord, "version"> & { version?: 1 },
): ResolvedRecoveryWrite {
  if (!store || !record.orgId || !record.draftId) return "unavailable";
  // A resolved record without a product cannot describe anything.
  if (!record.productId) return "conflict";
  const slot = readCreateRecoveryState(store, record.orgId, record.draftId);
  if (slot.state === "unreadable") return "unreadable";
  if (slot.state === "valid") {
    const existing = slot.record;
    if (existing.stage === "pending_create" && !existing.productId && existing.idemKey !== record.idemKey) {
      return "conflict";
    }
    if (existing.productId && existing.productId !== record.productId) {
      return "conflict";
    }
  }
  try {
    store.setItem(
      createRecoveryStorageKey(record.orgId, record.draftId),
      JSON.stringify({ ...record, version: 1 }),
    );
    return "written";
  } catch {
    return "unavailable";
  }
}

export function clearCreateRecovery(
  store: RecoveryStore | null,
  orgId: string | null | undefined,
  draftId: string | null | undefined,
): void {
  if (!store || !orgId || !draftId) return;
  try {
    store.removeItem(createRecoveryStorageKey(orgId, draftId));
  } catch {
    /* a stale record is harmless: it is scoped to a draft that no longer exists */
  }
}

/**
 * Canonical creation identity: one organisation + one persistent draft identity
 * is ONE creation identity. A genuinely new product therefore needs a new draft
 * identity (the wizard's existing "new draft" path mints one).
 *
 * The key is derived from stable inputs instead of being random per page
 * instance. Two tabs that load the same draft derive the same key without any
 * coordination, so the server's idempotency contract — not the browser — is
 * what prevents a second product.
 *
 * Contract compatibility (source evidence, not hosted proof):
 *   - `products.idempotency_key` is `text` with no length or format constraint
 *     (migration 0052), and `create_product` accepts `p_idempotency_key text`.
 *   - The `wizard-draft:` namespace cannot collide with the import path's
 *     `csv-sku:` / `csv-barcode:` / `csv-ref:` content-derived keys.
 *
 * Duplicate prevention nevertheless depends on the hosted server having the
 * unique index and the short-circuit lookup in the effective `create_product`.
 * The client alone cannot guarantee it.
 */
const CANONICAL_KEY_NAMESPACE = "wizard-draft";

export function deriveCanonicalCreateKey(
  orgId: string | null | undefined,
  draftId: string | null | undefined,
): string | null {
  const org = typeof orgId === "string" ? orgId.trim().toLowerCase() : "";
  const draft = typeof draftId === "string" ? draftId.trim().toLowerCase() : "";
  if (!org || !draft) return null;
  return `${CANONICAL_KEY_NAMESPACE}:${org}:${draft}`;
}

export type CreateKeySource = "existing_record" | "canonical";

/**
 * Choose the creation identity for this attempt.
 *
 * - A record that already carries a product never creates again.
 * - An unresolved `pending_create` record keeps ITS key. This is what preserves
 *   a legacy random key written by an earlier build; replacing it with a newly
 *   derived key would be a new creation identity, i.e. a possible duplicate.
 * - Otherwise derive the canonical key from `(orgId, draftId)`.
 *
 * A legitimate reload resumes the record's identity unconditionally. The
 * advisory `attemptId` gates clearing and key replacement, never reuse.
 */
export function resolveCanonicalCreateKey(params: {
  orgId: string | null | undefined;
  draftId: string | null | undefined;
  record: CreateRecoveryRecord | null;
}): { key: string | null; source: CreateKeySource | null } {
  if (!params.orgId || !params.draftId) return { key: null, source: null };
  if (params.record?.productId) return { key: null, source: null };
  if (params.record?.stage === "pending_create" && params.record.idemKey) {
    return { key: params.record.idemKey, source: "existing_record" };
  }
  return {
    key: deriveCanonicalCreateKey(params.orgId, params.draftId),
    source: "canonical",
  };
}

export function isCanonicalCreateKey(
  key: string | null | undefined,
  orgId: string | null | undefined,
  draftId: string | null | undefined,
): boolean {
  if (!key) return false;
  return key === deriveCanonicalCreateKey(orgId, draftId);
}

export type PendingRecoveryWrite = "written" | "conflict" | "unreadable" | "unavailable";

/**
 * Write the pre-submit record, refusing to destroy another creation identity.
 *
 * Refusals are conservative and cannot strand a draft, because the third
 * option — reuse the existing record's key and retry — always remains.
 *
 * An unreadable slot is refused: its bytes are the only record of an earlier
 * creation whose outcome cannot be established, so they are preserved rather
 * than replaced by a new (possibly second) identity.
 */
export function writePendingCreateRecovery(
  store: RecoveryStore | null,
  params: {
    orgId: string;
    draftId: string;
    productName: string;
    idemKey: string;
    attemptId?: string;
  },
): PendingRecoveryWrite {
  if (!store || !params.orgId || !params.draftId || !params.idemKey) return "unavailable";
  const slot = readCreateRecoveryState(store, params.orgId, params.draftId);
  if (slot.state === "unreadable") return "unreadable";
  const existing = slot.state === "valid" ? slot.record : null;
  if (existing) {
    // A product-bearing record must never be downgraded to an unresolved create.
    if (existing.productId) return "conflict";
    // Another unresolved creation identity owns this draft.
    if (existing.idemKey && existing.idemKey !== params.idemKey) return "conflict";
  }
  const record: CreateRecoveryRecord = {
    version: 1,
    orgId: params.orgId,
    draftId: params.draftId,
    productId: "",
    productName: params.productName || "",
    idemKey: params.idemKey,
    stage: "pending_create",
    createdAt: existing?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...(params.attemptId ? { attemptId: params.attemptId } : {}),
  };
  try {
    store.setItem(createRecoveryStorageKey(params.orgId, params.draftId), JSON.stringify(record));
    return "written";
  } catch {
    return "unavailable";
  }
}

export type RecoveryClearResult = "cleared" | "refused" | "none" | "unavailable";

export type RecoveryClearPolicy = "clear" | "retain";

/**
 * THE clearing policy. Every clear path in the wizard goes through this.
 *
 * A failed invocation does not establish that another earlier invocation with
 * the same creation key cannot commit. So a record may only be removed when
 * removing it cannot invent a new creation identity:
 *
 *   - a resolved (product-bearing) record is settled work: clearing is
 *     housekeeping, not an identity decision;
 *   - an unresolved record is only releasable when its key is CANONICAL, i.e.
 *     the exact same identity is derivable again from (orgId, draftId) and the
 *     next attempt is therefore submitted as the same create;
 *   - a legacy/random key can never be re-derived, so its record is retained
 *     under every error basis — definitive SQLSTATE, local validation and
 *     abandoned attempts alike.
 */
export function createRecoveryClearPolicy(params: {
  record: CreateRecoveryRecord;
  orgId: string | null | undefined;
  draftId: string | null | undefined;
}): RecoveryClearPolicy {
  const { record } = params;
  if (record.productId) return "clear";
  if (record.stage !== "pending_create") return "clear";
  return isCanonicalCreateKey(record.idemKey, params.orgId, params.draftId) ? "clear" : "retain";
}

/**
 * Clear a recovery record only when this attempt may legitimately do so.
 *
 * The ownership check stops one attempt erasing another attempt's key, and the
 * centralised policy stops ANY attempt erasing a non-canonical identity. An
 * unreadable slot is never removed: it cannot be interpreted, so it can never
 * be proven to belong to this attempt, and its bytes are evidence of a create
 * whose outcome is unknown.
 *
 * A record that already carries a product is resolved, so settling may clear it.
 */
export function clearCreateRecoveryOwned(
  store: RecoveryStore | null,
  params: {
    orgId: string | null | undefined;
    draftId: string | null | undefined;
    attemptKey: string;
    attemptId?: string;
  },
): RecoveryClearResult {
  if (!store || !params.orgId || !params.draftId) return "unavailable";
  const slot = readCreateRecoveryState(store, params.orgId, params.draftId);
  if (slot.state === "absent") return "none";
  if (slot.state === "unreadable") return "refused";
  const existing = slot.record;
  const ownsIdentity = existing.stage !== "pending_create" || existing.idemKey === params.attemptKey;
  if (!ownsIdentity) return "refused";
  if (createRecoveryClearPolicy({ record: existing, orgId: params.orgId, draftId: params.draftId }) === "retain") {
    return "refused";
  }
  try {
    store.removeItem(createRecoveryStorageKey(params.orgId, params.draftId));
    return "cleared";
  } catch {
    return "unavailable";
  }
}

/**
 * THE creation-eligibility gate. Checked before any identity derivation and
 * before any RPC submission, so an unresolved outcome can never be re-entered
 * as a brand-new create.
 *
 * There is deliberately NO acknowledgement input. Acknowledging an unreadable
 * record may change what the operator is told; it must never change whether
 * creation is allowed, must never alter the recovery slot, and is never
 * persisted as authorisation.
 *
 * An unresolved `pending_create` record does not block creation: it is the
 * resume path, and it keeps its existing key.
 */
export type CreateEligibility =
  | { allowed: true; state: "absent" | "valid"; record: CreateRecoveryRecord | null }
  | { allowed: false; reason: "unreadable" | "product_exists"; record: CreateRecoveryRecord | null; message: string };

export function evaluateCreateEligibility(params: {
  store: RecoveryStore | null;
  orgId: string | null | undefined;
  draftId: string | null | undefined;
}): CreateEligibility {
  const slot = readCreateRecoveryState(params.store, params.orgId, params.draftId);
  if (slot.state === "unreadable") {
    return {
      allowed: false,
      reason: "unreadable",
      record: null,
      message: recoveryNoticeMessage("recovery_unreadable"),
    };
  }
  if (slot.state === "valid" && slot.record.productId) {
    return {
      allowed: false,
      reason: "product_exists",
      record: slot.record,
      message: recoveryNoticeMessage("known_product"),
    };
  }
  return {
    allowed: true,
    state: slot.state,
    record: slot.state === "valid" ? slot.record : null,
  };
}

/** Operator-facing explanation for a restored hold. Never claims work succeeded. */
export function recoveryHoldMessage(stage: CreateRecoveryStage): string {
  if (stage === "pending_create") {
    return "A previous create for this draft did not report back, so its outcome is unknown. This draft keeps its existing creation identity, and creating again reuses that same identity. Check the Products page before assuming nothing was created.";
  }
  if (stage === "canonical") {
    return "This product already exists. Its catalogue details were not confirmed. Create will not run again for it — retry to finish the remaining steps.";
  }
  if (stage === "media") {
    return "This product already exists, but its images are still attached to the draft. Create will not run again for it — retry to move them across.";
  }
  return "This product already exists. Some post-create steps (supplier, channel listings or channel images) did not finish. Create will not run again for it — retry to finish them.";
}

/** The wizard step the restored hold must resume from. */
export function recoveryHoldStep(stage: CreateRecoveryStage): "canonical" | "media" | "post_create" {
  // `pending_create` has no product to resume; it is handled before a hold is built.
  if (stage === "pending_create") return "canonical";
  return stage;
}

/**
 * A created product always blocks another create for the same draft, whether
 * the record was just written or restored after a reload. A pending record also
 * blocks a *fresh* create: a new idempotency key would be a new product.
 */
export function recoveryBlocksCreate(record: CreateRecoveryRecord | null): boolean {
  if (!record) return false;
  return record.productId.length > 0 || record.stage === "pending_create";
}

/**
 * A pending record means "reuse this key", never "creation failed". The wizard
 * restores the key and retries the same create so the server can deduplicate.
 */
export function recoveryRequiresKeyReuse(record: CreateRecoveryRecord | null): boolean {
  return !!record && record.stage === "pending_create" && record.productId.length === 0;
}

/**
 * F-01 (Version 372): an empty lookup is NOT proof that the earlier RPC cannot
 * still commit. A timeout, a dropped connection or a statement still running on
 * the server can all end with the product being created after the response was
 * lost.
 *
 * This classifies the failure so the caller can tell the two cases apart:
 *   - `definitive` — a recognised code proves the transaction was rejected
 *     before it could commit (a PostgreSQL SQLSTATE raised by this call, or a
 *     PostgREST resolution-layer rejection). Absence really is absence.
 *   - `ambiguous`  — anything else (network, timeout, 5xx, contention, a unique
 *     violation whose row has not been read yet, an unrecognised code, or an
 *     unclassifiable error). The pending record and its key must be kept.
 *
 * Version 373: a codeless HTTP 4xx is NO LONGER definitive. An HTTP status only
 * says that some component answered the request; an intermediary (CDN, reverse
 * proxy, load balancer, WAF) can answer with a client-error status after the
 * upstream insert already committed. Treating that as proof of rollback cleared
 * the write-ahead record, which is the exact duplicate-product window this
 * module exists to close.
 */
export type CreateFailureKind = "definitive" | "ambiguous";

/**
 * What the classification actually rests on, so a report can state the evidence
 * instead of asserting a conclusion.
 */
export type CreateFailureBasis =
  | "sqlstate"
  | "postgrest_protocol"
  | "local_validation"
  | "unrecognised_code"
  | "transport_status"
  | "network"
  | "unknown";

export interface CreateFailureAssessment {
  kind: CreateFailureKind;
  /** SQLSTATE / PostgREST code when the error carried one. */
  code: string | null;
  message: string;
  basis: CreateFailureBasis;
}

/**
 * Errors that guarantee the transaction did not commit. These are raised while
 * the statement is being validated or executed, so PostgreSQL rolls it back.
 * They are raised by the server, so the outcome is known without a read.
 */
const DEFINITIVE_CODES = new Set<string>([
  "22P02", // invalid text representation
  "22001", // string too long
  "22003", // numeric out of range
  "22007", // invalid datetime format
  "22023", // invalid parameter value
  "23502", // not-null violation
  "23503", // foreign key violation
  "23514", // check violation
  "42501", // insufficient privilege
  "42703", // undefined column
  "42804", // datatype mismatch
  "42883", // undefined function
  "42P01", // undefined table
  "PGRST202", // function not found in schema cache
  "PGRST203", // overload ambiguity
  "PGRST204", // column not found
]);

/**
 * Errors that mean "this may already exist, or the transaction outcome is not
 * knowable from the error alone". A unique violation is explicitly NOT absence:
 * the key already belongs to a row that the caller must look up.
 */
const AMBIGUOUS_CODES = new Set<string>([
  "23505", // unique violation — the row exists, find it
  "40001", // serialization failure
  "40P01", // deadlock detected
  "55P03", // lock not available
  "57014", // statement cancelled / timeout
]);

const NETWORK_HINTS = [
  "failed to fetch",
  "networkerror",
  "network request failed",
  "network error",
  "load failed",
  "fetch failed",
  "err_name_not_resolved",
  "econnreset",
  "econnrefused",
  "etimedout",
  "enotfound",
  "socket hang up",
  "timed out",
  "timeout",
  "aborted",
  "gateway",
  "temporarily unavailable",
];

function readString(source: unknown, key: string): string | null {
  if (!source || typeof source !== "object") return null;
  const value = (source as Record<string, unknown>)[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function readStatusObject(source: unknown): number | null {
  if (!source || typeof source !== "object") return null;
  for (const key of ["status", "statusCode"]) {
    const value = (source as Record<string, unknown>)[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && /^\d{3}$/.test(value)) return Number(value);
  }
  return null;
}

/**
 * HTTP status, top level or nested. supabase-js commonly reports the transport
 * status under `context.status`, so both surfaces are read.
 *
 * This value only ever *describes* an error that carried no code. It is never
 * allowed to decide a classification by itself: an HTTP status says nothing
 * about the transaction, because an intermediary (CDN, reverse proxy, load
 * balancer, WAF) can answer with a 4xx after the upstream call already committed.
 */
function readTransportStatus(error: unknown): number | null {
  const direct = readStatusObject(error);
  if (direct !== null) return direct;
  if (!error || typeof error !== "object") return null;
  const record = error as Record<string, unknown>;
  for (const key of ["context", "cause", "error", "originalError"]) {
    const nested = readStatusObject(record[key]);
    if (nested !== null) return nested;
  }
  return null;
}

/**
 * Never returns `definitive` unless the error itself proves nothing committed.
 * An unclassifiable error is `ambiguous` on purpose: keeping a pending key is
 * recoverable, minting a second product is not.
 */
export function classifyCreateFailure(error: unknown): CreateFailureAssessment {
  const code = readString(error, "code");
  const message = error instanceof Error
    ? error.message || "The create did not complete."
    : readString(error, "message") ?? "The create did not complete.";

  /**
   * A client-side pre-flight rejection happens BEFORE any request is sent, so
   * nothing can have been created. It is definitive for a different reason than
   * a SQLSTATE: it is local provenance, not a server answer.
   */
  if (isLocalValidationError(error)) {
    return { kind: "definitive", code: null, message, basis: "local_validation" };
  }

  /**
   * A recognised code decides the outcome on its own, and it is resolved BEFORE
   * any transport status. A nested `context.status` therefore cannot downgrade
   * a code that already proves what happened.
   */
  if (code && DEFINITIVE_CODES.has(code)) {
    return {
      kind: "definitive",
      code,
      message,
      basis: code.startsWith("PGRST") ? "postgrest_protocol" : "sqlstate",
    };
  }
  if (code && AMBIGUOUS_CODES.has(code)) {
    return { kind: "ambiguous", code, message, basis: "sqlstate" };
  }
  if (code) {
    /**
     * A code this module cannot vouch for: an error re-raised from inside a
     * BEGIN…EXCEPTION wrapper, a provider-specific code, or a PostgREST status
     * code whose body was lost. None of those prove the call could not commit,
     * so the pending record stays.
     */
    return { kind: "ambiguous", code, message, basis: "unrecognised_code" };
  }

  /**
   * From here the error carries no code, so nothing proves the transaction was
   * rejected. That includes EVERY codeless 4xx: a bare client-error status only
   * says that some component answered the request, not that the insert rolled
   * back. 408 / 425 / 429 and every 5xx are equally non-committal.
   */
  const status = readTransportStatus(error);
  if (status !== null) {
    return { kind: "ambiguous", code: null, message, basis: "transport_status" };
  }

  const lower = message.toLowerCase();
  if (NETWORK_HINTS.some((hint) => lower.includes(hint))) {
    return { kind: "ambiguous", code: null, message, basis: "network" };
  }

  // Unknown shape: the write may still land.
  return { kind: "ambiguous", code: null, message, basis: "unknown" };
}

/**
 * What to do with the write-ahead `pending_create` record once the RPC threw.
 *
 * The only outcome that may clear write-ahead protection is a failure that
 * proves nothing committed, paired with a lookup that positively found nothing,
 * with no other invocation that could still be running under the same key. A
 * false-positive hold is recoverable; a cleared record mints a new key and a
 * duplicate product.
 */
export type CreateReconcileAction = "adopt_product" | "retain_pending" | "clear_pending";

export function decideCreateReconciliation(params: {
  kind: CreateFailureKind;
  lookupProductId: string | null;
  lookupOk: boolean;
  /**
   * True when an invocation that could have used this same key is still
   * unresolved — for example the wizard was abandoned while the RPC was in
   * flight, or a different attempt now owns the draft.
   */
  sameKeyAttemptUnresolved?: boolean;
}): CreateReconcileAction {
  // A product found by key is authoritative: resume it, never create again.
  if (params.lookupProductId) return "adopt_product";
  // The lookup itself failed, so absence was never established.
  if (!params.lookupOk) return "retain_pending";
  // Only a proven pre-commit rejection may clear write-ahead protection.
  if (params.kind !== "definitive") return "retain_pending";
  // …and only when nothing else could still be running under this key.
  if (params.sameKeyAttemptUnresolved) return "retain_pending";
  return "clear_pending";
}

/**
 * `setItem` returning without throwing is not enough — a locked-down or full
 * store can silently drop the record. Read the exact record back and compare
 * the identity fields that make it useful before submitting the create.
 */
export function confirmCreateRecoveryPersisted(
  store: RecoveryStore | null,
  expected: { orgId: string; draftId: string; idemKey: string },
): boolean {
  if (!store || !expected.orgId || !expected.draftId || !expected.idemKey) return false;
  const written = readCreateRecovery(store, expected.orgId, expected.draftId);
  return !!written
    && written.stage === "pending_create"
    && written.idemKey === expected.idemKey
    && written.productId.length === 0;
}

export interface PostCreateObligations {
  /** Channel steps that ended in a genuine failure and still need a retry. */
  unresolvedChannels: number;
  /** Images or draft assets that did not transfer. */
  mediaFailures: number;
  /** Supplier link has not been persisted yet. */
  supplierPending: boolean;
}

export function hasUnfinishedPostCreate(obligations: PostCreateObligations): boolean {
  return obligations.unresolvedChannels > 0 || obligations.mediaFailures > 0 || obligations.supplierPending;
}

/**
 * The draft is the only recovery context: it holds the channel selections,
 * per-account prices, chosen channel images and the draft media rows. While any
 * obligation is unresolved it must be kept, and a recovery record must exist so
 * a reload cannot start a second product.
 *
 * Once everything has settled the draft has no remaining purpose and is
 * discarded.
 */
export function shouldKeepDraftAfterCreate(obligations: PostCreateObligations): boolean {
  return hasUnfinishedPostCreate(obligations);
}

export function shouldDiscardDraftAfterCreate(obligations: PostCreateObligations): boolean {
  return !shouldKeepDraftAfterCreate(obligations);
}

/**
 * A draft that was just created against a real product must not be presented as
 * a fresh, creatable draft. Discard remains unavailable while the created
 * product still needs recovery; the operator can still abandon the create
 * attempt explicitly if they choose to.
 */
export function draftDiscardAllowed(params: {
  lock: { inFlight: boolean; productId?: string | null };
  record: CreateRecoveryRecord | null;
  /** The slot holds bytes that cannot be interpreted: never discard over them. */
  recoveryUnreadable?: boolean;
}): boolean {
  if (params.lock.inFlight) return false;
  if (params.recoveryUnreadable) return false;
  if (params.record && recoveryBlocksCreate(params.record)) return false;
  return true;
}

/**
 * Whether a reloaded wizard can rebuild a retryable attempt from the draft.
 * The form/variation/stock/channel data always comes from the persisted draft;
 * media assets come from the draft media rows.
 *
 * `hasDraftMedia` may be omitted while the media rows are still loading — the
 * media check is then deferred to retry time rather than guessed.
 */
export function canRebuildAttemptFromDraft(params: {
  stage: CreateRecoveryStage;
  hasForm: boolean;
  hasDraftMedia?: boolean;
}): { ok: boolean; reason: string | null } {
  if (!params.hasForm) {
    return { ok: false, reason: "The saved draft no longer contains the product details, so the remaining steps cannot be retried here. Finish them on the product page." };
  }
  if (params.stage === "media" && params.hasDraftMedia === false) {
    return { ok: false, reason: "The draft images are no longer available, so they cannot be transferred. Add them on the product page." };
  }
  return { ok: true, reason: null };
}

export type RecoveryNoticeKind =
  | "pending_unknown"
  | "known_product"
  | "validation_failure"
  | "rejected_before_commit"
  | "identity_retained"
  | "storage_unavailable"
  | "attempt_resumed"
  | "recovery_unreadable"
  | "recovery_write_conflict";

/**
 * Notice copy for the wizard path (D-04). These must render even when no
 * post-create hold exists, and must never assert a guarantee the client cannot
 * verify (the server-side idempotency enforcement is a separate gate).
 */
export function recoveryNoticeMessage(kind: RecoveryNoticeKind): string {
  if (kind === "pending_unknown") {
    return "This draft's create did not report back and its outcome is unknown. The draft is kept, along with its creation identity, so a retry is submitted as the same create. Check the Products page before assuming nothing was created.";
  }
  if (kind === "known_product") {
    return "This draft already produced a product, so it is not a fresh draft. Open that product to finish anything still outstanding.";
  }
  if (kind === "validation_failure") {
    return "The last create was rejected on this device before anything was sent, so nothing was created by this attempt. Correct the details and try again — a retry is submitted as the same creation identity.";
  }
  if (kind === "rejected_before_commit") {
    return "The server rejected the last create before it could commit, and a check found no product for this draft. Correct the details and try again — a retry is submitted as the same creation identity.";
  }
  if (kind === "identity_retained") {
    return "This draft keeps its earlier creation identity. An earlier attempt that used that identity may still be finishing, so the record was left in place and was not replaced with a new one. Check the Products page before assuming nothing was created.";
  }
  if (kind === "storage_unavailable") {
    return "This browser could not store the recovery record, so a reload cannot resume this create. Confirm the outcome on the Products page before creating again.";
  }
  if (kind === "attempt_resumed") {
    return "This draft resumed an earlier create attempt. Its existing creation identity was reused; the draft was not restarted.";
  }
  if (kind === "recovery_unreadable") {
    return "An earlier create was recorded for this draft, but this browser can no longer read that record, so its outcome cannot be established. Creating again stays blocked for this draft while the record exists. Nothing was deleted — the draft and the record are preserved, and you can leave this page safely.";
  }
  return "The recovery record for this draft could not be updated, because another window holds a different creation identity for it. The existing record was left unchanged, so a reload may not resume this attempt. Check the Products page before assuming nothing was created.";
}

/**
 * What the operator may do with the draft right now.
 *
 * Leaving the screen and abandoning the creation are different things: leaving
 * changes no persisted state, while abandoning would need its own authoritative
 * design (an outcome lookup first).
 */
export type DraftExitState = "product_exists" | "creation_unresolved" | "recovery_unreadable" | "clear";

export function draftExitState(params: {
  lock: { inFlight: boolean; productId?: string | null };
  record: CreateRecoveryRecord | null;
  recoveryUnreadable?: boolean;
}): DraftExitState {
  if (params.lock.productId || params.record?.productId) return "product_exists";
  // Unreadable is not absent: the draft keeps its (unknown) creation identity,
  // so it is treated exactly like an unresolved creation for exit purposes.
  if (params.recoveryUnreadable) return "recovery_unreadable";
  if (params.record?.stage === "pending_create") return "creation_unresolved";
  return "clear";
}